'use client';
import {useEffect, useState} from 'react';
import {
  GoBookmark,
  GoBookmarkFill,
  GoCheckCircleFill,
  GoXCircleFill,
} from 'react-icons/go';

import LoginModal from '../LoginModal';
import Button from '../Button';

import useUserContext from '@/hooks/useUserContext';
import {IModalsDictionary} from '@/dictionaries/components/modals/types';
import {cn} from '@/lib/utils';
import useGlobalContext from '@/hooks/useGlobalContext';
import {authenticatedFetch} from '@/lib/autheticatedFetch';
import {useToast} from '@/hooks/useToast';

type Props = {
  label: string;
  removeLabel: string;
  dict: IModalsDictionary;
  lang: string;
  className?: string;
  surroundingInfos: {
    id: number;
    title: string;
  };
  userSavedSurroundings: GuideSurroundingPage[];
};

export default function PlayYourTripButton({
  label,
  removeLabel,
  dict,
  lang,
  className,
  surroundingInfos,
  userSavedSurroundings,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const {isLogged} = useUserContext();
  const {userHasTripSaved, setUserHasTripSabed} = useGlobalContext();
  const {toast} = useToast();

  async function handleSurrounding() {
    setIsLoading(true);
    try {
      const {ok} = await authenticatedFetch({
        url: `/customers/me/guide-surroundings/${surroundingInfos.id}`,
        method: userHasTripSaved ? 'DELETE' : 'POST',
      });

      if (ok && !userHasTripSaved) {
        toast({
          description: (
            <div className="flex items-center gap-2">
              <GoCheckCircleFill className="w-4 h-4" />
              <span>{surroundingInfos.title} added to your trip.</span>
            </div>
          ),
          variant: 'success',
        });
        setUserHasTripSabed(true);
      }
      if (ok && userHasTripSaved) {
        toast({
          description: (
            <div className="flex items-center gap-2">
              <GoCheckCircleFill className="w-4 h-4" />
              <span>Removed from your trip.</span>
            </div>
          ),
          variant: 'success',
        });
        setUserHasTripSabed(false);
      }
      if (!ok) {
        toast({
          description: (
            <div className="flex items-center gap-2">
              <GoXCircleFill className="w-4 h-4" />
              <span>Oops, there was an error during add to your trip.</span>
            </div>
          ),
        });
      }
    } catch (error) {
      toast({
        description: (
          <div className="flex items-center gap-2">
            <GoXCircleFill className="w-4 h-4" />
            <span>Oops, there was an error during add to your trip.</span>
          </div>
        ),
      });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (isLogged) {
      const surroundingIsSaved = userSavedSurroundings.some(
        (surrounding) => surrounding.id === surroundingInfos.id,
      );
      if (surroundingIsSaved) {
        setUserHasTripSabed(true);
      }
    }
  }, [isLogged]);

  if (!isLogged) {
    return (
      <LoginModal dict={dict} lang={lang}>
        <Button variant="outline" className={cn('gap-x-6 whitespace-nowrap w-fit', className)}>
          <GoBookmark className="w-6 h-6" />
          {label}
        </Button>
      </LoginModal>
    );
  }

  return (
    <Button
      disabled={isLoading}
      onClick={async () => handleSurrounding()}
      variant={userHasTripSaved ? 'default' : 'outline'}
      className={cn('gap-x-6 w-fit whitespace-nowrap', className)}>
      {userHasTripSaved ? (
        <>
          <GoBookmarkFill
            className={cn('w-6 h-6', {
              'animate-infinite-horizontal-flip': isLoading,
            })}
          />
          {userHasTripSaved ? removeLabel : label}
        </>
      ) : (
        <>
          <GoBookmark
            className={cn('w-6 h-6', {
              'animate-infinite-horizontal-flip': isLoading,
            })}
          />
          {userHasTripSaved ? removeLabel : label}
        </>
      )}
    </Button>
  );
}
